import { Popover, Transition } from '@headlessui/react';
import { IconBounceRight } from '@tabler/icons-react';
import React, { memo, useCallback, useMemo } from 'react';
import { SpeakerLabelTransitionEffect } from '../../../libs/transitions/types';
import AnimationTypeSelector from './AnimationTypeSelector';
import TimingControls from './TimingControls';
import { updateSpeakerLabelStyles } from '@/stores/clip';
import PlayerMenuTooltip from '@/App/remotion/components/PlayerMenus/PlayerMenuTooltip';
import { Clip } from '@/domains/asset';

function SpeakerLabelAnimationMenu({ clip }: { clip: Clip }) {
  const transitions = useMemo(() => {
    const existingTransitions = clip.asset_metadata.speaker_label_styles.transitions;
    return {
      in: SpeakerLabelTransitionEffect.None,
      out: SpeakerLabelTransitionEffect.None,
      showAfter: 0,
      hideAfter: undefined,
      duration: 0.3,
      showAgainOnNewSpeaker: true,
      ...existingTransitions
    };
  }, [clip.asset_metadata.speaker_label_styles.transitions]);

  const handleTransitionChange = useCallback(
    (value: SpeakerLabelTransitionEffect) => {
      updateSpeakerLabelStyles(clip.id, {
        transitions: {
          ...transitions,
          in: value,
          out: value
        }
      });
    },
    [clip.id, transitions]
  );

  const handleShowAfterChange = useCallback(
    (value: number) => {
      updateSpeakerLabelStyles(clip.id, {
        transitions: {
          ...transitions,
          showAfter: value
        }
      });
    },
    [clip.id, transitions]
  );

  const handleHideAfterChange = useCallback(
    (value: number | undefined) => {
      updateSpeakerLabelStyles(clip.id, {
        transitions: {
          ...transitions,
          hideAfter: value
        }
      });
    },
    [clip.id, transitions]
  );

  const handleShowAgainOnNewSpeakerChange = useCallback(
    (value: boolean) => {
      updateSpeakerLabelStyles(clip.id, {
        transitions: {
          ...transitions,
          showAgainOnNewSpeaker: value
        }
      });
    },
    [clip.id, transitions]
  );

  return (
    <Popover>
      {({ open }) => (
        <>
          <Popover.Button
            data-testid="speaker-label-animation-button"
            className="group/button flex size-10 items-center justify-center rounded-md hover:bg-slate-100"
          >
            <div className="flex w-10 cursor-pointer items-center justify-center rounded-md">
              <IconBounceRight size={20} />
              <PlayerMenuTooltip content="Animation" />
            </div>
          </Popover.Button>
          <Transition
            show={open}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Popover.Panel className="flex justify-start">
              <div className="absolute -right-24 mt-1.5 flex w-64 flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 text-sm shadow-lg">
                <div>
                  <div className="mb-4">
                    <AnimationTypeSelector
                      value={transitions.in || SpeakerLabelTransitionEffect.None}
                      onChange={handleTransitionChange}
                    />
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <TimingControls
                      showAfter={transitions.showAfter}
                      hideAfter={transitions.hideAfter}
                      showAgainOnNewSpeaker={transitions.showAgainOnNewSpeaker}
                      onShowAfterChange={handleShowAfterChange}
                      onHideAfterChange={handleHideAfterChange}
                      onShowAgainOnNewSpeakerChange={handleShowAgainOnNewSpeakerChange}
                    />
                  </div>
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default memo(SpeakerLabelAnimationMenu);
